import React from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { ArrowLeft, X } from 'lucide-react';
import { IconButton } from '@/components/ui';

export interface PageShellProps {
    title: React.ReactNode;
    subtitle?: React.ReactNode;
    /** Route to go back to; omit to use history */
    backTo?: string;
    onBack?: () => void;
    /** Show an X instead of the back arrow */
    close?: boolean;
    /** Right side of the header row */
    actions?: React.ReactNode;
    /** Pinned CTA bar */
    footer?: React.ReactNode;
    /** Wrap children in the default scroll container with 20px gutters */
    padded?: boolean;
    /** Dark (plum-950) chrome for camera screens */
    dark?: boolean;
    className?: string;
    children: React.ReactNode;
}

/**
 * T3 task-flow skeleton: back · title header, scrolling content, pinned footer.
 * Pages give it `className="lg:..."` tweaks or supply their own columns on lg.
 */
export const PageShell: React.FC<PageShellProps> = ({ title, subtitle, backTo, onBack, close = false, actions, footer, padded = true, dark = false, className, children }) => {
    const navigate = useNavigate();
    const back = onBack ?? (() => (backTo ? navigate(backTo) : navigate(-1)));
    return (
        <div className={clsx('flex h-full min-h-0 flex-1 flex-col', dark ? 'bg-primary-950 text-white' : 'bg-surface-page', className)}>
            <header className="flex shrink-0 items-center gap-2.5 px-5 pb-3 pt-2 lg:px-7 lg:pt-[22px]">
                <IconButton variant={dark ? 'onDark' : 'card'} label="Back" onClick={back}>
                    {close ? <X /> : <ArrowLeft />}
                </IconButton>
                <div className="min-w-0 flex-1">
                    <h1 className="truncate text-[20px] lg:text-[22px] font-black leading-tight tracking-[-0.01em]">{title}</h1>
                    {subtitle && <p className={clsx('truncate text-xs', dark ? 'text-primary-200' : 'text-ink-muted')}>{subtitle}</p>}
                </div>
                {actions}
            </header>
            <div className={clsx('min-h-0 flex-1', padded && 'overflow-y-auto px-5 lg:px-7')}>{children}</div>
            {footer && (
                <footer className={clsx('shrink-0 border-t px-5 pb-[max(26px,env(safe-area-inset-bottom))] pt-3.5 lg:px-7', dark ? 'border-white/10 bg-primary-950' : 'border-line-subtle bg-surface-card')}>
                    {footer}
                </footer>
            )}
        </div>
    );
};
export default PageShell;
